/**
 * Copyright © 2025 Delicious AI, LLC
 */

import { LazyQueryExecFunction } from '@apollo/client'
import { CommonProductHelpers } from '@dai/common'

import { GetUpcDictQuery, GetUpcDictQueryVariables } from '@/graphql'
import { ProductHelpers } from '@/utils/products.helpers'

export interface Product {
  upc: string
  name: string
  facingQuantity: number
}

export interface Shelf {
  shelf: number
  products: Product[]
}

export interface ImportedPlanogram {
  door: number
  shelves: Shelf[]
  displayId?: string
  missingUpcs?: string
}

export const validateAndConvertPlanograms = async (
  json:
    | { door: number; shelves: Shelf[] }[]
    | { door: number; shelves: Shelf[] },
  getUpcDict: LazyQueryExecFunction<GetUpcDictQuery, GetUpcDictQueryVariables>,
  t: (key: string, params?: any) => string,
): Promise<{
  planograms: ImportedPlanogram[]
  error: string | null
}> => {
  try {
    // Normalize to array
    const jsonArray = Array.isArray(json) ? json : [json]

    const validatedPlanograms: ImportedPlanogram[] = await Promise.all(
      jsonArray.map(async (item, index) => {
        if (typeof item.door !== 'number') {
          throw new Error(
            `Door at index ${index} is missing a valid door number`,
          )
        }
        if (!Array.isArray(item.shelves)) {
          throw new Error(`Door ${item.door} is missing shelves array`)
        }

        // Validate and sort shelves
        const shelves: Shelf[] = item.shelves.map(
          (shelf: Shelf, shelfIndex: number) => {
            if (typeof shelf.shelf !== 'number') {
              throw new Error(
                `Door ${item.door}, shelf at index ${shelfIndex} is missing a valid shelf number`,
              )
            }
            if (!Array.isArray(shelf.products)) {
              throw new Error(
                `Door ${item.door}, shelf ${shelf.shelf} is missing products array`,
              )
            }

            // Validate products
            shelf.products.forEach((product: any, prodIndex: number) => {
              if (!product.upc || typeof product.upc !== 'string') {
                throw new Error(
                  `Door ${item.door}, shelf ${shelf.shelf}, product at index ${prodIndex} is missing valid UPC`,
                )
              }
              if (
                typeof product.facingQuantity !== 'number' ||
                product.facingQuantity < 1
              ) {
                throw new Error(
                  `Door ${item.door}, shelf ${shelf.shelf}, product ${product.upc} has invalid facingQuantity`,
                )
              }
            })

            return shelf
          },
        )

        // Sort shelves by shelf number
        shelves.sort((a: Shelf, b: Shelf) => a.shelf - b.shelf)

        // Validate that the UPC is valid and convert to 12-digit format
        const invalidUpcs: Set<string> = new Set()
        const almostFinalShelves = shelves.map((shelf: Shelf) => ({
          ...shelf,
          products: shelf.products.map(product => {
            const upc = CommonProductHelpers.convertTo12DigitUPC(product.upc)
            if (upc) {
              return {
                ...product,
                upc,
              }
            } else {
              invalidUpcs.add(product.upc)
              return {
                ...product,
                upc: product.upc,
              }
            }
          }),
        }))

        const orderedInvalidUpcs = Array.from(invalidUpcs).sort()

        // Lookup invalid UPCs in the database
        const upcDictResults = await getUpcDict({
          variables: {
            upcs: orderedInvalidUpcs,
          },
        })

        const invalidUpcDict = Object.fromEntries(
          (upcDictResults.data?.upcDict ?? []).map(e => [e.upc, e.matches]),
        )

        const missingUpcs: string[] = orderedInvalidUpcs.filter(
          upc => invalidUpcDict[upc]?.length === 0,
        )

        const finalShelves = almostFinalShelves.map(shelf => {
          return {
            ...shelf,
            products: shelf.products.map(product => {
              return {
                ...product,
                upc:
                  invalidUpcs.has(product.upc) &&
                  invalidUpcDict[product.upc]?.length === 1
                    ? invalidUpcDict[product.upc]![0]
                    : product.upc.length === 12 // Already converted to 12 digits
                      ? product.upc
                      : ProductHelpers.UNKNOWN_UPC,
              }
            }),
          }
        })

        return {
          door: item.door,
          shelves: finalShelves,
          displayId: undefined,
          missingUpcs: missingUpcs.join(', '),
        }
      }),
    )

    // Sort doors by door number
    const sortedPlanograms = validatedPlanograms.sort((a, b) => a.door - b.door)

    // Check for missing UPCs and create error message
    let errorMessage: string | null = null
    if (sortedPlanograms.some(p => p.missingUpcs)) {
      errorMessage = `${t('importPlanogram.upcsNotFound')} ${sortedPlanograms
        .filter(p => p.missingUpcs)
        .map(p => p.missingUpcs)
        .join(', ')}`
    }

    return {
      planograms: sortedPlanograms,
      error: errorMessage,
    }
  } catch (err) {
    return {
      planograms: [],
      error:
        err instanceof Error
          ? err.message
          : t('importPlanogram.invalidJSONFormat'),
    }
  }
}
